import {
  CharacterRef,
  Position,
  TextRef,
  RiggedCharacterRef,
} from '../../../types/typing.ts';
import React from 'react';
import { gsap } from 'gsap';

export const animateCharacterAlongPath = (
  item: CharacterRef | RiggedCharacterRef,
  onUpdateCharacter: (
    id: string,
    properties: { position?: Position; scale?: number; pathPoints?: Position[] }
  ) => void
) => {
  if (!item || item.pathPoints.length <= 1) return;
  const duration = 5;
  const initialPosition = {
    x: item.position.x,
    y: item.position.y,
  };

  const target = (item as any).container || (item as any).sprite;

  // Early return if no valid animation target exists
  if (!target) {
    console.warn(
      'animateCharacterAlongPath: No valid target (container/sprite) found'
    );
    return;
  }

  // Kill any existing animations
  gsap.killTweensOf(target);
  const timeline = gsap.timeline({
    onComplete: function () {
      onUpdateCharacter(item.id, {
        position: {
          x: initialPosition.x,
          y: initialPosition.y,
        },
      });

      gsap.set(target, {
        x: initialPosition.x,
        y: initialPosition.y,
      });
      target.position.x = initialPosition.x;
      target.position.y = initialPosition.y;
    },
  });

  const startPoint = item.pathPoints[0];
  gsap.set(target, {
    x: startPoint.x,
    y: startPoint.y,
  });

  const segmentDuration = duration / (item.pathPoints.length - 1);
  for (let i = 0; i < item.pathPoints.length - 1; i++) {
    const nextPoint = item.pathPoints[i + 1];

    timeline.to(target, {
      x: nextPoint.x,
      y: nextPoint.y,
      duration: segmentDuration,
      ease: 'none', // Linear movement between points
      onUpdate: function () {
        if (target && target.position) {
          target.position.x = this.targets()[0].x;
          target.position.y = this.targets()[0].y;
        }
      },
    });
  }

  return timeline;
};

export const animateTextAlongPath = (
  item: TextRef,
  onUpdateText: (id: string, textProps: Partial<TextRef>) => void
) => {
  if (!item || item.pathPoints.length <= 1) return;
  const duration = 5;

  const initialPosition = {
    x: item.position.x,
    y: item.position.y,
  };
  // Kill any existing animations
  gsap.killTweensOf(item.textContainer);
  const timeline = gsap.timeline({
    onComplete: function () {
      onUpdateText(item.id, {
        position: {
          x: initialPosition.x,
          y: initialPosition.y,
        },
      });

      gsap.set(item.textContainer, {
        x: initialPosition.x,
        y: initialPosition.y,
      });
      item.textContainer.position.x = initialPosition.x;
      item.textContainer.position.y = initialPosition.y;
    },
  });

  const startPoint = item.pathPoints[0];
  gsap.set(item.textContainer, {
    x: startPoint.x,
    y: startPoint.y,
  });

  const segmentDuration = duration / (item.pathPoints.length - 1);
  for (let i = 0; i < item.pathPoints.length - 1; i++) {
    const nextPoint = item.pathPoints[i + 1];

    timeline.to(item.textContainer, {
      x: nextPoint.x,
      y: nextPoint.y,
      duration: segmentDuration,
      ease: 'none', // Linear movement between points
      onUpdate: function () {
        if (item.textContainer && item.textContainer.position) {
          item.textContainer.position.x = this.targets()[0].x;
          item.textContainer.position.y = this.targets()[0].y;
        }
      },
    });
  }

  return timeline;
};

export const generatePathPointsForCharacter = (
  charactersRef: React.MutableRefObject<
    Map<string, CharacterRef | RiggedCharacterRef>
  >,
  id: string,
  onUpdateCharacter: (
    id: string,
    properties: {
      position?: { x: number; y: number };
      scale?: number;
      pathPoints?: Position[];
    }
  ) => void,
  animationOption: any
) => {
  const selectedCharacter = Array.from(charactersRef.current.values()).find(
    (character) => character.id === id
  ) as CharacterRef | RiggedCharacterRef | undefined;

  if (!selectedCharacter) return;

  const index = Array.from(charactersRef.current.keys()).find(
    (key) => charactersRef.current.get(key) === selectedCharacter
  );

  if (index === undefined) return;

  const initialX = selectedCharacter.position.x;
  const initialY = selectedCharacter.position.y;

  const pathPoints: Array<{ x: number; y: number }> = [];

  const numPoints = 60; // Adjust based on desired smoothness

  if (animationOption.type === 'simple') {
    const movement = animationOption.movement;

    for (let i = 0; i <= numPoints; i++) {
      const progress = i / numPoints;
      pathPoints.push({
        x: initialX + movement.x * progress,
        y: initialY + movement.y * progress,
      });
    }
  } else if (animationOption.type === 'complex') {
    switch (animationOption.animationType) {
      case 'circle':
        const radius = animationOption.radius;

        pathPoints.push({
          x: initialX,
          y: initialY,
        });

        for (let i = 0; i <= numPoints; i++) {
          const progress = i / numPoints;
          const angle = progress * Math.PI * 2;
          pathPoints.push({
            x: initialX + radius - Math.cos(angle) * radius,
            y: initialY + Math.sin(angle) * radius,
          });
        }
        break;

      case 'stairs':
        const stepSize = animationOption.stepSize;
        const steps = animationOption.steps || 4;
        let currentX = initialX;
        let currentY = initialY;

        for (let i = 0; i < steps; i++) {
          const horizontalPoints = 10; // Points per step segment
          for (let j = 0; j <= horizontalPoints; j++) {
            const progress = j / horizontalPoints;
            pathPoints.push({
              x: currentX + stepSize.x * progress,
              y: currentY,
            });
          }
          currentX += stepSize.x;

          const verticalPoints = 10; // Points per step segment
          for (let j = 0; j <= verticalPoints; j++) {
            const progress = j / verticalPoints;
            pathPoints.push({
              x: currentX,
              y: currentY + stepSize.y * progress,
            });
          }
          currentY += stepSize.y;
        }
        break;

      case 'diagonal':
        const movement = animationOption.movement;

        for (let i = 0; i <= numPoints; i++) {
          const progress = i / numPoints;
          pathPoints.push({
            x: initialX + movement.x * progress,
            y: initialY + movement.y * progress,
          });
        }
        break;

      case 'bounce':
        const height = animationOption.height || 200;
        const bounces = animationOption.bounces || 3;

        const pointsPerBounce = Math.floor(numPoints / bounces);

        for (let bounce = 0; bounce < bounces; bounce++) {
          // Generate points for one bounce cycle
          for (let i = 0; i <= pointsPerBounce; i++) {
            const progress = i / pointsPerBounce;

            // Simulate bounce using sine wave
            // For the down part of the bounce
            if (progress < 0.5) {
              const bounceProgress = progress * 2; // 0 to 1 for first half
              pathPoints.push({
                x: initialX,
                y: initialY + height * bounceProgress,
              });
            }
            // For the up part of the bounce
            else {
              const bounceProgress = (progress - 0.5) * 2; // 0 to 1 for second half
              // Use easeOutQuad for more realistic bounce
              const easeOut = 1 - (1 - bounceProgress) * (1 - bounceProgress);
              pathPoints.push({
                x: initialX,
                y:
                  initialY +
                  height -
                  height * easeOut * (bounce < bounces - 1 ? 1 : 0),
              });
            }
          }
        }
        break;
    }
  }
  selectedCharacter.pathPoints = pathPoints;
  //onUpdateCharacter(selectedCharacter.id, { pathPoints: pathPoints });
  const animation = animateCharacterAlongPath(
    selectedCharacter,
    onUpdateCharacter
  );
  animation?.play();
};

export const generatePathPointsForText = (
  textRef: React.MutableRefObject<Map<string, TextRef>>,
  animationOption: any,
  onUpdateText: any
) => {
  const selectedText = Array.from(textRef.current.values()).find(
    (text) => text.isSelected
  );

  if (!selectedText) return;

  const index = Array.from(textRef.current.keys()).find(
    (key) => textRef.current.get(key) === selectedText
  );

  if (index === undefined) return;

  const initialX = selectedText.position.x;
  const initialY = selectedText.position.y;

  const pathPoints: Array<{ x: number; y: number }> = [];

  const numPoints = 60; // Adjust based on desired smoothness

  if (animationOption.type === 'simple') {
    const movement = animationOption.movement;

    for (let i = 0; i <= numPoints; i++) {
      const progress = i / numPoints;
      pathPoints.push({
        x: initialX + movement.x * progress,
        y: initialY + movement.y * progress,
      });
    }
  } else if (animationOption.type === 'complex') {
    switch (animationOption.animationType) {
      case 'circle':
        const radius = animationOption.radius;

        for (let i = 0; i <= numPoints; i++) {
          const progress = i / numPoints;
          const angle = progress * Math.PI * 2 - Math.PI / 2;
          pathPoints.push({
            x: initialX + radius - Math.cos(angle) * radius,
            y: initialY + Math.sin(angle) * radius,
          });
        }
        break;

      case 'stairs':
        const stepSize = animationOption.stepSize;
        const steps = animationOption.steps || 4;
        let currentX = initialX;
        let currentY = initialY;

        for (let i = 0; i < steps; i++) {
          const horizontalPoints = 10; // Points per step segment
          for (let j = 0; j <= horizontalPoints; j++) {
            const progress = j / horizontalPoints;
            pathPoints.push({
              x: currentX + stepSize.x * progress,
              y: currentY,
            });
          }
          currentX += stepSize.x;

          const verticalPoints = 10; // Points per step segment
          for (let j = 0; j <= verticalPoints; j++) {
            const progress = j / verticalPoints;
            pathPoints.push({
              x: currentX,
              y: currentY + stepSize.y * progress,
            });
          }
          currentY += stepSize.y;
        }
        break;

      case 'diagonal':
        const movement = animationOption.movement;

        for (let i = 0; i <= numPoints; i++) {
          const progress = i / numPoints;
          pathPoints.push({
            x: initialX + movement.x * progress,
            y: initialY + movement.y * progress,
          });
        }
        break;

      case 'bounce':
        const height = animationOption.height || 200;
        const bounces = animationOption.bounces || 3;

        const pointsPerBounce = Math.floor(numPoints / bounces);

        for (let bounce = 0; bounce < bounces; bounce++) {
          for (let i = 0; i <= pointsPerBounce; i++) {
            const progress = i / pointsPerBounce;

            // Simulate bounce using sine wave
            // For the down part of the bounce
            if (progress < 0.5) {
              const bounceProgress = progress * 2; // 0 to 1 for first half
              pathPoints.push({
                x: initialX,
                y: initialY + height * bounceProgress,
              });
            }
            // For the up part of the bounce
            else {
              const bounceProgress = (progress - 0.5) * 2; // 0 to 1 for second half
              // Use easeOutQuad for more realistic bounce
              const easeOut = 1 - (1 - bounceProgress) * (1 - bounceProgress);
              pathPoints.push({
                x: initialX,
                y:
                  initialY +
                  height -
                  height * easeOut * (bounce < bounces - 1 ? 1 : 0),
              });
            }
          }
        }
        break;
    }
  }
  selectedText.pathPoints = pathPoints;
  const animation = animateTextAlongPath(selectedText, onUpdateText);
  animation?.play();
};
